import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerRepo, invoiceRepo, quotationRepo, receiptRepo, productRepo, ORDER_TYPE } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import PaymentQrModal from "../../components/PaymentQrModal";
import BrandMark from "../../components/BrandMark";
import { PrintArea } from "../../components/TaxDocumentPaper";
import { useBranding } from "../../config/branding";
import { uid } from "../../mock/store";
import { nextDocNo } from "../../services/docNumber";
import { settleInvoicePayment } from "../../services/settlement";
import { taxDocumentService } from "../../services/taxDocument";
import { onlinePaymentService } from "../../services/onlinePayment";
import { payUrl } from "../../config/online-payment";
import { calcVat } from "../../utils/thaiTax";
import {
  Button, DataTable, Drawer, Input, Modal, PageHead, Select, StatusBadge, Tabs, baht, formatDate, useAsync, useTableState,
} from "../../components/ui";

const newLine = () => ({ lineId: uid("ln"), name: "", price: 0, quantity: 1 });

// กระดาษเอกสารเดิม (ใบแจ้งหนี้ / ใบเสนอราคา / ใบเสร็จ) — ใช้ทั้งใน Drawer และตอนพิมพ์
function DocPaper({ preview, brand, tx }) {
  const { row } = preview;
  const items = row.items || [{ name: tx(`ชำระตามใบแจ้งหนี้ ${row.invoiceNo || ""}`, `Payment for invoice ${row.invoiceNo || ""}`), quantity: 1, total: row.total }];
  return (
    <div className="doc-paper" style={{ padding: 16, boxShadow: "none", ...brand.cssVars }}>
      {/* ส่วนหัวเอกสาร: แบรนด์ร้าน (White-label) หรือชื่อธุรกิจ — ไม่แสดงชื่อ LucidCRM บนเอกสารของร้านที่ใช้แบรนด์เอง */}
      {brand.custom ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <BrandMark brand={brand} size={40} />
          <div className="u-strong" style={{ fontSize: 16 }}>{brand.name}</div>
        </div>
      ) : null}
      <div className="doc-paper__head">
        <div>
          <div className="u-strong u-fs-18">{row.customerName}</div>
          <div className="u-fs-13 u-muted">{row.docNo} · {formatDate(row.createdAt)}</div>
        </div>
        <StatusBadge status={row.status || "paid"} />
      </div>
      {items.map((item, index) => (
        <div key={index} className="u-between u-fs-13" style={{ padding: "6px 0", borderBottom: "1px solid var(--c-slate-100)" }}>
          <span>{item.name} x{item.quantity}</span>
          <span>{baht(item.total)}</span>
        </div>
      ))}
      <div className="doc-total">
        {row.subtotal !== undefined ? <div className="doc-total__row"><span>{tx("รวม", "Subtotal")}</span><span>{baht(row.subtotal)}</span></div> : null}
        {row.discount !== undefined ? <div className="doc-total__row"><span>{tx("ส่วนลด", "Discount")}</span><span>-{baht(row.discount)}</span></div> : null}
        <div className="doc-total__row doc-total__row--grand"><span>{tx("ยอดสุทธิ", "Total")}</span><span>{baht(row.total)}</span></div>
      </div>
    </div>
  );
}

export default function Billing() {
  const { orgId, organization, toast, hasModule, profile } = useApp();
  const { tx } = useI18n();
  const navigate = useNavigate();
  const [printToken, setPrintToken] = useState(0);
  const [linkInfo, setLinkInfo] = useState(null);
  const by = profile?.email || profile?.fullname || "";
  const [tab, setTab] = useState("invoices");
  const tableState = useTableState();
  const [createOpen, setCreateOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);
  const [preview, setPreview] = useState(null);
  const brand = useBranding();

  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);
  const { data: products } = useAsync(() => productRepo.all(orgId), [orgId], []);

  const repos = { invoices: invoiceRepo, quotations: quotationRepo, receipts: receiptRepo };
  const repo = repos[tab];

  const { data, loading, reload } = useAsync(
    () => repo.list(orgId, { page: tableState.page, perPage: tableState.perPage, search: tableState.search, sortBy: "createdAt", sortDir: "desc" }),
    [orgId, tab, tableState.page, tableState.perPage, tableState.search]
  );

  const [form, setForm] = useState({ customerId: "", lines: [newLine()], discount: 0, dueDays: 7 });

  const addLine = () => setForm((prev) => ({ ...prev, lines: [...prev.lines, newLine()] }));
  const setLine = (index, patch) => setForm((prev) => ({ ...prev, lines: prev.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) }));
  const removeLine = (index) => setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== index) }));

  // คำนวณ VAT เป็นสตางค์ ปัดครั้งเดียวระดับเอกสาร (ราคาที่กรอกยังไม่รวม VAT)
  const { subtotal, total } = calcVat({ lines: form.lines.map((line) => ({ price: line.price, quantity: line.quantity || 1 })), discount: form.discount, mode: "exclusive" });

  const create = async () => {
    const customer = customers?.find((c) => c.id === form.customerId);
    if (!customer || !form.lines.length) return toast(tx("เลือกลูกค้าและกรอกรายการให้ครบ", "Select customer and items"), "error");

    const items = form.lines.map((line) => ({ name: line.name, price: line.price, quantity: line.quantity, total: Math.round(line.price * 100 * line.quantity) / 100 }));
    // เลขที่เรียงอัตโนมัติ (ต่อร้าน+ประเภท+ปี) — เอกสารเก่าที่เป็นเลขแบบเดิมไม่ถูกแก้
    const { docNo } = await nextDocNo({ orgId, organization, docType: tab === "invoices" ? "invoice" : "quotation" });
    const payload = {
      docNo,
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.phone,
      items,
      subtotal,
      discount: Number(form.discount || 0),
      vatPercent: 7,
      total,
      branchId: customer.branchId,
      ...(tab === "invoices"
        ? { status: "unpaid", dueAt: new Date(Date.now() + form.dueDays * 86400000).toISOString(), paidAmount: 0 }
        : { status: "draft", validUntil: new Date(Date.now() + form.dueDays * 86400000).toISOString() }),
    };

    await repo.create(orgId, payload);
    setCreateOpen(false);
    setForm({ customerId: "", lines: [newLine()], discount: 0, dueDays: 7 });
    reload();
    toast(tx("สร้างเอกสารเรียบร้อย", "Document created"), "success");
  };

  // ปิดบิลหลังรับเงินสำเร็จ — ผ่าน settleInvoicePayment (กันซ้ำด้วย transactionId, ออกเลขใบเสร็จ, ร้านมี e-Tax จะได้ฉบับร่างใบกำกับ)
  const markPaid = async (invoice, qr) => {
    const result = await settleInvoicePayment({ orgId, invoiceId: invoice.id, transactionId: qr?.transactionId, amount: qr?.amount, refNo: qr?.refNo, organization, by });
    reload();
    toast(
      result.taxDraft
        ? tx("รับชำระแล้ว ออกใบเสร็จแล้ว และสร้างใบกำกับภาษีฉบับร่างให้ตรวจที่เมนู e-Tax", "Paid. Receipt issued and a tax invoice draft was created in e-Tax")
        : tx("รับชำระเงินและออกใบเสร็จเรียบร้อย", "Payment received, receipt issued"),
      "success"
    );
  };

  const makeTaxDraft = async (source, row) => {
    try {
      const invoice = source === "invoice" ? row : await invoiceRepo.get(orgId, row.invoiceId);
      if (!invoice) throw new Error(tx("ไม่พบใบแจ้งหนี้ต้นทางของใบเสร็จนี้", "Source invoice not found"));
      const active = await taxDocumentService.activeForInvoice(orgId, invoice.id);
      const doc = active || (await taxDocumentService.createDraft({ orgId, organization, source, invoice, receipt: source === "receipt" ? row : undefined, by }));
      navigate(`/admin/etax?open=${doc.id}`);
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const sendLink = async (invoice) => {
    try {
      const link = await onlinePaymentService.linkForInvoice({ orgId, invoice });
      setLinkInfo({ invoice, link, url: payUrl(link.code) });
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(tx("คัดลอกแล้ว", "Copied"), "success");
    } catch (e) {
      toast(tx("คัดลอกไม่สำเร็จ", "Copy failed"), "error");
    }
  };

  const columnsByTab = {
    invoices: [
      { key: "docNo", label: tx("เลขที่", "No.") },
      { key: "customerName", label: tx("ลูกค้า", "Customer") },
      { key: "total", label: tx("ยอดรวม", "Total"), align: "right", render: (row) => baht(row.total) },
      { key: "dueAt", label: tx("ครบกำหนด", "Due"), render: (row) => formatDate(row.dueAt) },
      { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
      {
        key: "action",
        label: "",
        render: (row) => (
          <div className="u-flex u-gap-4">
            <Button size="sm" onClick={() => setPreview({ type: "invoice", row })}>{tx("ดู", "View")}</Button>
            {row.status !== "paid" ? <Button size="sm" variant="primary" onClick={() => setPayInvoice(row)}>{tx("รับชำระ", "Collect")}</Button> : null}
            {row.status !== "paid" && hasModule("online_payment") ? <Button size="sm" onClick={() => sendLink(row)}>{tx("ส่งลิงก์ชำระ/QR", "Payment link")}</Button> : null}
            {row.status === "paid" && hasModule("etax") ? <Button size="sm" onClick={() => makeTaxDraft("invoice", row)}>{tx("ออกใบกำกับภาษี", "Tax invoice")}</Button> : null}
          </div>
        ),
      },
    ],
    quotations: [
      { key: "docNo", label: tx("เลขที่", "No.") },
      { key: "customerName", label: tx("ลูกค้า", "Customer") },
      { key: "total", label: tx("ยอดรวม", "Total"), align: "right", render: (row) => baht(row.total) },
      { key: "validUntil", label: tx("ใช้ได้ถึง", "Valid until"), render: (row) => formatDate(row.validUntil) },
      { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
      { key: "action", label: "", render: (row) => <Button size="sm" onClick={() => setPreview({ type: "quotation", row })}>{tx("ดู", "View")}</Button> },
    ],
    receipts: [
      { key: "docNo", label: tx("เลขที่", "No.") },
      { key: "invoiceNo", label: tx("อ้างอิงใบแจ้งหนี้", "Invoice ref") },
      { key: "customerName", label: tx("ลูกค้า", "Customer") },
      { key: "total", label: tx("ยอดรวม", "Total"), align: "right", render: (row) => baht(row.total) },
      { key: "paidAt", label: tx("ชำระเมื่อ", "Paid at"), render: (row) => formatDate(row.paidAt) },
      {
        key: "action",
        label: "",
        render: (row) => (
          <div className="u-flex u-gap-4">
            <Button size="sm" onClick={() => setPreview({ type: "receipt", row })}>{tx("ดู/พิมพ์", "View / print")}</Button>
            {hasModule("etax") ? <Button size="sm" onClick={() => makeTaxDraft("receipt", row)}>{tx("ออกใบกำกับภาษี", "Tax invoice")}</Button> : null}
          </div>
        ),
      },
    ],
  };

  return (
    <>
      <PageHead
        title={tx("ใบเสนอราคา / ใบแจ้งหนี้ / ใบเสร็จ", "Quotation / Invoice / Receipt")}
        actions={tab !== "receipts" ? <Button variant="primary" onClick={() => setCreateOpen(true)}>+ {tx("สร้างเอกสาร", "New document")}</Button> : null}
      />

      <Tabs
        tabs={[
          { key: "invoices", label: tx("ใบแจ้งหนี้", "Invoices") },
          { key: "quotations", label: tx("ใบเสนอราคา", "Quotations") },
          { key: "receipts", label: tx("ใบเสร็จ", "Receipts") },
        ]}
        value={tab}
        onChange={(next) => {
          setTab(next);
          tableState.setPage(1);
        }}
      />

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีเอกสาร", "No documents")}
        columns={columnsByTab[tab]}
      />

      <Modal
        open={createOpen}
        size="lg"
        title={tab === "invoices" ? tx("สร้างใบแจ้งหนี้", "New invoice") : tx("สร้างใบเสนอราคา", "New quotation")}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={create}>{tx("บันทึก", "Save")}</Button>
          </>
        }
      >
        <Select label={tx("ลูกค้า", "Customer")} placeholder={tx("เลือกลูกค้า", "Select customer")} options={(customers || []).map((c) => ({ value: c.id, label: c.name }))} value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })} />

        <div className="u-strong u-fs-13 u-mb-8">{tx("รายการ", "Line items")}</div>
        {form.lines.map((line, index) => (
          <div key={line.lineId} className="grid-2" style={{ gridTemplateColumns: "1fr 100px 80px 32px", alignItems: "end", gap: 8 }}>
            <Select placeholder={tx("เลือกรายการ", "Select item")} options={(products || []).map((p) => ({ value: p.name, label: p.name }))} value={line.name} onChange={(e) => {
              const product = products?.find((p) => p.name === e.target.value);
              setLine(index, { name: e.target.value, price: product?.price || line.price });
            }} />
            <Input type="number" placeholder={tx("ราคา", "Price")} value={line.price} onChange={(e) => setLine(index, { price: Number(e.target.value) })} />
            <Input type="number" placeholder={tx("จำนวน", "Qty")} value={line.quantity} onChange={(e) => setLine(index, { quantity: Number(e.target.value) })} />
            <Button size="sm" variant="danger" onClick={() => removeLine(index)}>×</Button>
          </div>
        ))}
        <Button size="sm" onClick={addLine} className="u-mb-16">+ {tx("เพิ่มรายการ", "Add line")}</Button>

        <div className="grid-2">
          <Input label={tx("ส่วนลด", "Discount")} type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })} />
          <Input label={tx("ครบกำหนด/ใช้ได้ถึง (วัน)", "Valid/due (days)")} type="number" value={form.dueDays} onChange={(e) => setForm({ ...form, dueDays: Number(e.target.value) })} />
        </div>

        <div className="doc-total">
          <div className="doc-total__row"><span>{tx("รวมก่อนภาษี", "Subtotal")}</span><span>{baht(subtotal)}</span></div>
          <div className="doc-total__row"><span>{tx("ส่วนลด", "Discount")}</span><span>-{baht(form.discount)}</span></div>
          <div className="doc-total__row doc-total__row--grand"><span>{tx("ยอดสุทธิ (รวม VAT 7%)", "Total (incl. VAT 7%)")}</span><span>{baht(total)}</span></div>
        </div>
      </Modal>

      <Drawer
        open={!!preview}
        title={preview?.row?.docNo}
        onClose={() => setPreview(null)}
        footer={preview ? <Button variant="primary" onClick={() => setPrintToken(Date.now())}>{tx("พิมพ์", "Print")}</Button> : null}
      >
        {preview ? <DocPaper preview={preview} brand={brand} tx={tx} /> : null}
      </Drawer>
      <PrintArea token={printToken} onDone={() => setPrintToken(0)}>
        {preview ? <DocPaper preview={preview} brand={brand} tx={tx} /> : null}
      </PrintArea>

      <Modal
        open={!!linkInfo}
        size="sm"
        title={tx("ลิงก์ชำระเงินออนไลน์", "Online payment link")}
        onClose={() => setLinkInfo(null)}
        footer={<Button variant="primary" onClick={() => setLinkInfo(null)}>{tx("ปิด", "Close")}</Button>}
      >
        {linkInfo ? (
          <>
            <div className="u-strong">{linkInfo.invoice.docNo} · {baht(linkInfo.invoice.total)}</div>
            <div className="u-fs-13 u-muted u-mb-8">{tx("ส่งลิงก์นี้ให้ลูกค้า เปิดแล้วสแกน QR จ่ายได้เลย ไม่ต้องล็อกอิน · หมดอายุ", "Send this link to the customer — no login needed · expires")} {formatDate(linkInfo.link.expireAt)}</div>
            <div className="card-crm" style={{ wordBreak: "break-all", fontSize: 13 }}>{linkInfo.url}</div>
            <div className="u-flex u-gap-4 u-mt-8">
              <Button size="sm" onClick={() => copyText(linkInfo.url)}>{tx("คัดลอกลิงก์", "Copy link")}</Button>
              <a className="btn-crm" target="_blank" rel="noopener noreferrer" href={`https://line.me/R/msg/text/?${encodeURIComponent(tx(`ชำระ ${linkInfo.invoice.docNo} ได้ที่ ${linkInfo.url}`, `Pay ${linkInfo.invoice.docNo}: ${linkInfo.url}`))}`}>{tx("ส่งทาง LINE", "Share via LINE")}</a>
              <Button size="sm" onClick={() => { setPayInvoice(linkInfo.invoice); setLinkInfo(null); }}>{tx("แสดง QR ที่เคาน์เตอร์", "Show QR here")}</Button>
            </div>
          </>
        ) : null}
      </Modal>

      <PaymentQrModal
        open={!!payInvoice}
        title={tx(`ชำระ ${payInvoice?.docNo}`, `Pay ${payInvoice?.docNo}`)}
        subtitle={payInvoice?.customerName}
        amount={payInvoice?.total || 0}
        orderType={ORDER_TYPE.invoice}
        reference={payInvoice?.docNo}
        items={[{ productName: payInvoice?.docNo, quantity: 1, price: payInvoice?.total || 0 }]}
        orgId={orgId}
        onClose={() => setPayInvoice(null)}
        onPaid={(qr) => markPaid(payInvoice, qr)}
      />
    </>
  );
}
